import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';

export const goalsFeatureKeys = createQueryKeys('goalsFeature', {
  goalList: ({ siteId, search, limit, order, sort }) => [{ siteId, search, limit, order: { field: order, sort } }],
  goalSimpleList: ({ siteId }) => [{ siteId }],
  goalDetail: ({ id, presetDate, startDate, endDate }) => [{ id, presetDate, startDate, endDate }],
  goalDistribution: ({ id, startDate, endDate, timeUnit }) => [{ id, startDate, endDate, timeUnit }],
  goalCounts: ({ id, startDate, endDate }) => [{ id, startDate, endDate }],
  goalPageConversions: ({ id, siteId, startDate, endDate, search, page, perPage, order }) => [
    { id, siteId, startDate, endDate, search, page, perPage, order },
  ],
});

export const goalListQuery = ({ siteId, search, limit, order, sort }) => ({
  ...goalsFeatureKeys.goalList({ siteId, search, limit, order, sort }),
  getNextPageParam: (lastPage) => (lastPage.goalList.hasNextPage ? lastPage.goalList.nextPageCursor : undefined),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalList($siteId: Int, $cursor: String, $search: String, $limit: Int, $order: GoalListOrder) {
        goalList(site: $siteId, cursor: $cursor, search: $search, limit: $limit, order: $order) {
          list {
            __typename
            triggers {
              id
              name
              type
              valueType
              autoName
              fixedValue {
                currency
                worth
              }
              elementValue {
                currency
                condition {
                  elementSelectorType
                  onAnyPage
                  onPage
                  pageUrl
                  selectorForDesktop
                  selectorForPhone
                  selectorForTablet
                  trackingOption
                  wildcardUrl
                }
              }
              filter
            }
            id
            siteId
            name
            abTestsCount
            abTestsCountRunning
            abTestsCountStopped
            abTestsCountActive
            abTestsCountTotal
            conversionsCount
            createdAt
            createdBy {
              id
              login
              name
            }
            displayData
            icon
            permissions {
              canView
              canEdit
              canDelete
              canShare
            }
            sessionsCount
            uiMode
          }
          hasNextPage
          nextPageCursor
        }
      }
    `,
  },
});

export const goalSimpleListQuery = ({ siteId }) => ({
  ...goalsFeatureKeys.goalSimpleList({ siteId }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalSimpleList($siteId: Int!) {
        goalSimpleList(site: $siteId) {
          id
          siteId
          name
          hasValue
        }
      }
    `,
  },
});

export const GOAL_NAME_ONLY = gql`
  query GoalDetail($id: Int!) {
    goalDetail(id: $id) {
      id
      name
    }
  }
`;

export const goalDetailQuery = ({ id, startDate, endDate, presetDate }) => ({
  ...goalsFeatureKeys.goalDetail({ id, startDate, endDate, presetDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDetail($id: Int!, $startDate: Int, $endDate: Int, $presetDate: String) {
        goalDetail(id: $id, startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
          __typename
          id
          name
          abTestsCount
          abTestsCountRunning
          abTestsCountStopped
          abTestsCountActive
          abTestsCountTotal
          autoName
          createdAt
          createdBy {
            id
            name
            login
          }
          icon
          deletedAt
          displayData
          conversionsCount
          permissions {
            canView
            canEdit
            canDelete
            canShare
          }
          purpose
          triggers {
            autoName
            createdAt
            deletedAt
            filter
            id
            name
            uiMode
            valueType
            type
            fixedValue {
              worth
              currency
            }
            elementValue {
              currency
              condition {
                trackingOption
                elementSelectorType
                pageUrl
                wildcardUrl
                onPage
                onAnyPage
                selectorForDesktop
                selectorForTablet
                selectorForPhone
              }
            }
          }
          siteId
          syncValues
          hasValue
          totalValue {
            displayWorth
            displayCurrency
          }
          abTests(startDate: $startDate, endDate: $endDate, presetDate: $presetDate) {
            id
            duration
            publishedAt
            stoppedAt
            createdAt
            createdByUserName
            name
            pageUrl
            status
            type
            totalConversions
            totalVisitors
            variants {
              id
              redirectUrl
              variantName
              results {
                conversions
              }
            }
            variantsCount
          }
        }
      }
    `,
  },
});

export const goalPageConversionsQuery = ({ id, siteId, startDate, endDate, search, page, perPage, order }) => ({
  ...goalsFeatureKeys.goalPageConversions({
    id,
    siteId,
    startDate,
    endDate,
    search,
    page,
    perPage,
    order,
  }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalPageConversions(
        $id: Int!
        $siteId: Int!
        $startDate: Int
        $endDate: Int
        $search: String
        $page: Int
        $perPage: Int
        $order: GoalPageConversionsOrder
      ) {
        goalPageConversions(
          id: $id
          siteId: $siteId
          startDate: $startDate
          endDate: $endDate
          search: $search
          page: $page
          perPage: $perPage
          order: $order
        ) {
          list {
            pageUrl
            totalConversions
            totalVisitors
            totalValue {
              displayWorth
              displayCurrency
            }
          }
          meta {
            page
            perPage
            total
            totalPages
          }
        }
      }
    `,
  },
});

export const goalDistributionQuery = ({ id, startDate, endDate, timeUnit }) => ({
  ...goalsFeatureKeys.goalDistribution({ id, startDate, endDate, timeUnit }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalDistribution($id: Int!, $startDate: Int, $endDate: Int, $timeUnit: TimeUnit!) {
        goalDistribution(id: $id, startDate: $startDate, endDate: $endDate, timeUnit: $timeUnit) {
          conversionsCount
          uniqueConversionsCount
          startAt
          endAt
          totalValue {
            displayWorth
            displayCurrency
          }
          triggers {
            id
            conversionsCount
            uniqueConversionsCount
            totalValue {
              displayWorth
              displayCurrency
            }
          }
        }
      }
    `,
  },
});

export const goalCountsQuery = ({ id, startDate, endDate }) => ({
  ...goalsFeatureKeys.goalCounts({ id, startDate, endDate }),
  useErrorBoundary: false,
  meta: {
    query: gql`
      query GoalCounts($id: Int!, $startDate: Int, $endDate: Int) {
        goalCounts(id: $id, startDate: $startDate, endDate: $endDate) {
          conversionsCount
          uniqueConversionsCount
          totalValue {
            displayWorth
            displayCurrency
          }
          triggers {
            id
            conversionsCount
            uniqueConversionsCount
            totalValue {
              displayWorth
              displayCurrency
            }
          }
        }
      }
    `,
  },
});
